import _ from 'lodash';

/**
 * Stores all modifierGroups that have been loaded for an item as well as the fields of the item
 * Handles the entire process of obtaining an item's modifiers.
 *
 * Is the one true source of an item's modifierGroups and options.
 *
 * Injections:
 *   $http : to make the API request for the modifierGroups
 *   modifierHierarchyHelper : to reset the modifierGroups to their defaults
 */
export function ItemDetailsService($http, $q, modifierHierarchyHelper, restaurantInfoFactory) {
    'ngInject';
    /**
     * itemDetails will store all item's that have had their modifierGroups loaded
     *
     * Format: itemDetails[GROUP_ID][ITEM_ID] = [ITEM_OBJECT]
     */
    const itemDetails = {};
    const getModifierGroupsAsyncPromises = {};

    const service = {
        setItemDetailByGuid,
        getItemDetailByGuid,
        getDecoratedItemDetailByGuid,
        getModifierGroupsByGuidAsync,

        // Exposed for testing
        decorateModifiers,
        deselectAll,
        _setItemDetail,
        _getItemDetail,
        _getDecoratedItemDetail
    };

    return service;

    /**
     * setItemDetail(): stores the given item and loads its modifierGroups
     * @returns {Promise<void>} resolved once the item and its modifierGroups have been saved in our cache
     */
    // TODO Delete when unit tests are refactored to use setItemDetailByGuid()
    function _setItemDetail(groupIdString, item) {
        return _loadModifierGroups(groupIdString, item.idString)
            .then(modifierGroups => {
                _setItemDetailHandler(modifierGroups, item, groupIdString, item.idString);
            });
    }

    function setItemDetailByGuid(groupGuid, item) {
        return getModifierGroupsByGuidAsync(groupGuid, item.guid)
            .then(modifierGroups => {
                _setItemDetailHandler(modifierGroups, item, groupGuid, item.guid);
            });
    }

    /**
     *
     * @param {*} modifierGroups response from `_loadModifierGroups()`
     * @param {*} item passed through from `setItemDetail()`'s argument
     * @param {*} groupKey group's id or guid
     * @param {*} itemKey item's id or guid
     */
    function _setItemDetailHandler(modifierGroups, item, groupKey, itemKey) {
        var i;

        // Initialize object if undefined
        if (!itemDetails[groupKey]) {
            itemDetails[groupKey] = {};
        }

        // Store fields on the item
        itemDetails[groupKey][itemKey] = angular.copy(item);

        // Store the loaded modifierGroup so we don't need to request again
        itemDetails[groupKey][itemKey].modifierGroups = modifierGroups;

        // Set the default selections on the modifier groups and options
        for (i = 0; i < itemDetails[groupKey][itemKey].modifierGroups.length; i++) {
            modifierHierarchyHelper.initializeModifierHierarchy(itemDetails[groupKey][itemKey].modifierGroups[i]);
        }
    }

    /**
     * getItemDetail(): returns item information for a given item id
     *   -> we return a copy of the master
     */
    // TODO Delete when unit tests are refactored to use getItemDetailByGuid()
    function _getItemDetail(groupIdString, itemId) {
        return _getItemDetailByKey(groupIdString, itemId);
    }

    function getItemDetailByGuid(groupGuid, itemGuid) {
        return _getItemDetailByKey(groupGuid, itemGuid);
    }

    function _getItemDetailByKey(groupKey, itemKey) {
        if (_getItemDetailExistsByKey(groupKey, itemKey)) {
            return angular.copy(itemDetails[groupKey][itemKey]);
        }
    }

    /**
     *
     * @param {*} groupGuid
     * @param {*} itemGuid
     * @returns {Promise<*>} promise that will resolve with a copy of the item's modifier groups once they have loaded
     */
    function getModifierGroupsByGuidAsync(groupGuid, itemGuid) {
        if (!getModifierGroupsAsyncPromises[groupGuid]) {
            getModifierGroupsAsyncPromises[groupGuid] = {};
        }

        if (!getModifierGroupsAsyncPromises[groupGuid][itemGuid]) {
            getModifierGroupsAsyncPromises[groupGuid][itemGuid] = _loadModifierGroupsByGuid(groupGuid, itemGuid);
        }

        return getModifierGroupsAsyncPromises[groupGuid][itemGuid]
            .then(modifierGroups => _.cloneDeep(modifierGroups));
    }

    /**
     *
     * @param {*} groupKey
     * @param {*} itemKey
     * @returns {boolean} whether or not a cache entry exists for the given group+item
     */
    function _getItemDetailExistsByKey(groupKey, itemKey) {
        return itemDetails[groupKey] && itemDetails[groupKey][itemKey];
    }

    /**
     * Get the corresponding item detail and consolidates it with the selections made based on the cartItem
     *
     * @returns a _new_ decorated item detail
     */
    // TODO Delete when unit tests are refactored to use getDecoratedItemDetailByGuid()
    function _getDecoratedItemDetail(cartItem) {
        return _getDecoratedItemDetailImpl('groupIdString', 'idString', cartItem);
    }

    function getDecoratedItemDetailByGuid(cartItem) {
        return _getDecoratedItemDetailImpl('groupGuid', 'guid', cartItem);
    }

    function _getDecoratedItemDetailImpl(groupKeyProperty, itemKeyProperty, cartItem) {
        var i,
            newItemDetail;

        if (_getItemDetailExistsByKey(cartItem[groupKeyProperty], cartItem[itemKeyProperty])) {
            // Make a copy. We should not tamper with the master copy else we have to worry about resetting it
            newItemDetail = angular.copy(_getItemDetailByKey(cartItem[groupKeyProperty], cartItem[itemKeyProperty]));

            if (newItemDetail.quantity > 1) {
                newItemDetail.price /= newItemDetail.quantity;
            }

            newItemDetail.quantity = cartItem.quantity;
            newItemDetail.specialRequest = cartItem.specialRequest;
            newItemDetail.inventoryCount = cartItem.inventoryCount;
            newItemDetail.uniqueIdString = cartItem.uniqueIdString;

            _decorateModifiers(itemKeyProperty, newItemDetail, cartItem);

            // Update hierarchy flags
            for (i = 0; i < newItemDetail.modifierGroups.length; i++) {
                modifierHierarchyHelper.updateModifierHierarchy(newItemDetail.modifierGroups[i]);
            }
            return newItemDetail;
        }
    }

    /**
     * node is either a modifierGroup or option, it will have child options or modifierGroups, respectively.
     * We recurse through each child of the given node, as long as the child id also exists in the decorator node
     */
    function decorateModifiers(node, decoratorNode) {
        _decorateModifiers('idString', node, decoratorNode);
    }

    /**
     *
     * @param {string} keyProperty the property name to use for the map key
     * @param {*} node
     * @param {*} decoratorNode
     */
    function _decorateModifiers(keyProperty, node, decoratorNode) {
        var i,
            // Maps a decorator group or decorator option's id to it's index in the decoratorNode
            decoratorMap = {},
            decoratorNodeIndex;

        if (!node || !decoratorNode) {
            return;
        }

        if (decoratorNode.hasOwnProperty('modifierGroups')) {
            /**
             * Creating the mapping from id to index. This is to check whether a particular node's child
             * should be decorated further. Also we can avoid an n^2 runtime if we were just to do a nested loop
             */
            for (i = 0; i < decoratorNode.modifierGroups.length; i++) {
                decoratorMap[decoratorNode.modifierGroups[i][keyProperty]] = i;
            }

            for (i = 0; i < node.modifierGroups.length; i++) {
                decoratorNodeIndex = decoratorMap[node.modifierGroups[i][keyProperty]];
                if (decoratorNodeIndex !== undefined) {
                    _decorateModifiers(keyProperty, node.modifierGroups[i], decoratorNode.modifierGroups[decoratorNodeIndex]);
                } else if (decoratorNode.modifierGroups.length === 0 && node.modifierGroups[i].hasOwnProperty('options')) {
                    // in the case that there are no modifier decorators specified, this means that no option is selected
                    // since decorateModifiers is only ever called from the scope of itemEdited we do not
                    // need to worry about default selections
                    deselectAll(node.modifierGroups[i]);
                }
            }
        } else if (decoratorNode.hasOwnProperty('options')) {
            for (i = 0; i < decoratorNode.options.length; i++) {
                decoratorMap[decoratorNode.options[i][keyProperty]] = i;
            }

            for (i = 0; i < node.options.length; i++) {
                decoratorNodeIndex = decoratorMap[node.options[i][keyProperty]];
                if (decoratorNodeIndex !== undefined) {
                    node.options[i].selected = true;
                    node.options[i].inventoryCount = decoratorNode.options[decoratorNodeIndex].inventoryCount;
                    node.options[i].quantity = decoratorNode.options[decoratorNodeIndex].quantity;
                    // Open selected options with children
                    if (node.options[i].modifierGroups.length > 0) {
                        node.options[i].opened = true;
                    }

                    _decorateModifiers(keyProperty, node.options[i], decoratorNode.options[decoratorNodeIndex]);
                } else if (node.maxSelections === decoratorNode.options.length && node.maxSelections !== 1) {
                    // disable buttons if the options are NOT single select
                    node.options[i].disabled = true;
                } else {
                    // All modifiers that are not decorated should not be selected
                    // This accounts for deselcting default modifiers
                    node.options[i].selected = false;
                }
            }
        }
    }

    /**
     * deselectAll(node): sets all node options to deselcted.
     */
    function deselectAll(node) {
        for (var i = 0; i < node.options.length; i++) {
            node.options[i].selected = false;
        }
    }

    /**
     * _sortModifierGroups(): sort the modifier group. Required comes first followed by optional groups
     *  -> add Special Requests as an optional group
     * @param modifierGroups response entity from server's modifiergroups endpoint
     * @param onlineOrderInfo `onlineOrder` data from restaurantInfoFactory
     */
    function _sortModifierGroups(modifierGroups, onlineOrderInfo) {
        var i,
            requiredModifierGroups = [],
            optionalModifierGroups = [],
            sortedModifierGroups;

        for (i = 0; i < modifierGroups.length; i++) {
            // Check if the modifierGroup is required
            if (modifierGroups[i].minSelections > 0) {
                requiredModifierGroups.push(modifierGroups[i]);
            } else {
                optionalModifierGroups.push(modifierGroups[i]);
            }
        }

        // Append the arrays to get the sorted list of groups
        sortedModifierGroups = requiredModifierGroups.concat(optionalModifierGroups);

        // Add Special Request if it's configured
        if (onlineOrderInfo.allowSpecialRequests) {
            sortedModifierGroups.push({
                id: 'specialRequest',
                name: 'Special Request',
                isSpecialRequest: true,
                placeHolderMessage: onlineOrderInfo.specialRequestsMessage
            });
        }

        return sortedModifierGroups;
    }

    /**
     * _loadModifierGroups(): makes an API call to get the modifierGroups of a given item
     * @returns {Promise} result of `_sortModifierGroups()`
     */
    function _loadModifierGroups(groupIdString, itemId) {
        const requestUrl = 'items/' + itemId + '/modifiergroups?groupId=' + groupIdString;
        return _loadModifierGroupsImpl(requestUrl);
    }

    function _loadModifierGroupsByGuid(groupGuid, itemGuid) {
        const requestUrl = 'itemsbyguid/' + itemGuid + '/modifiergroupsbyguid?groupGuid=' + groupGuid;
        return _loadModifierGroupsImpl(requestUrl);
    }

    function _loadModifierGroupsImpl(requestUrl) {
        requestUrl += '&fulfillmentDate=' + new Date().toISOString();
        const httpDfd = $http.get(requestUrl);
        const onlineOrderingInfoDfd = restaurantInfoFactory.getInfo(['onlineOrder']);

        return $q.all([httpDfd, onlineOrderingInfoDfd])
            .then(function(response) {
                return _sortModifierGroups(response[0].data, response[1].onlineOrder);
            });
    }
}
